import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LottoHoroscope = () => {
  const [selectedZodiac, setSelectedZodiac] = useState('');
  const [selectedStar, setSelectedStar] = useState('');
  const [selectedGame, setSelectedGame] = useState('lotto');
  const [generatedNumbers, setGeneratedNumbers] = useState([]);
  const [generatedExtraNumbers, setGeneratedExtraNumbers] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [starAnimation, setStarAnimation] = useState(false);
  const [activeStarEffect, setActiveStarEffect] = useState('');

  // Konfiguracja gier
  const gameConfigs = {
    'lotto': {
      name: 'Lotto',
      mainNumbers: { count: 6, max: 49 },
      extraNumbers: null,
      color: 'from-yellow-500 to-orange-500'
    },
    'miniLotto': {
      name: 'Mini Lotto',
      mainNumbers: { count: 5, max: 42 },
      extraNumbers: null,
      color: 'from-green-500 to-emerald-500'
    },
    'eurojackpot': {
      name: 'Eurojackpot',
      mainNumbers: { count: 5, max: 50 },
      extraNumbers: { count: 2, max: 12 },
      color: 'from-blue-500 to-purple-500'
    },
    'multiMulti': {
      name: 'Multi Multi',
      mainNumbers: { count: 20, max: 80 },
      extraNumbers: null,
      color: 'from-purple-500 to-pink-500'
    },
    'kaskada': {
      name: 'Kaskada',
      mainNumbers: { count: 12, max: 36 },
      extraNumbers: null,
      color: 'from-indigo-500 to-blue-500'
    },
    'keno': {
      name: 'Keno',
      mainNumbers: { count: 10, max: 70 },
      extraNumbers: null,
      color: 'from-red-500 to-pink-500'
    }
  };

  // Konfiguracja gwiazd z unikalnymi efektami
  const starConfigs = {
    'aldebaran': {
      name: 'Aldebaran 🔴',
      icon: '🔴',
      color: 'text-red-400',
      effect: 'red-pulse',
      description: 'Czerwony gigant - gwiazda siły i determinacji'
    },
    'betelgeuse': {
      name: 'Betelgeuse ⭐',
      icon: '⭐',
      color: 'text-orange-400',
      effect: 'supernova',
      description: 'Supergigant - gwiazda wielkiej mocy i transformacji'
    },
    'rigel': {
      name: 'Rigel 💙',
      icon: '💙',
      color: 'text-blue-400',
      effect: 'blue-shimmer',
      description: 'Niebieski nadgigant - gwiazda mądrości i jasności'
    },
    'vega': {
      name: 'Vega ✨',
      icon: '✨',
      color: 'text-white',
      effect: 'star-rain',
      description: 'Gwiazda polarna - przewodnik szczęścia'
    },
    'antares': {
      name: 'Antares 🌟',
      icon: '🌟',
      color: 'text-red-500',
      effect: 'fire-spin',
      description: 'Czerwony supergigant - gwiazda pasji i energii'
    },
    'spica': {
      name: 'Spica 💫',
      icon: '💫',
      color: 'text-blue-300',
      effect: 'light-beam',
      description: 'Podwójna gwiazda - symbol harmonii i równowagi'
    },
    'orion': {
      name: 'Orion 🌌',
      icon: '🌌',
      color: 'text-purple-400',
      effect: 'constellation',
      description: 'Konstelacja Oriona - gwiazda myśliwego i odwagi'
    },
    'deneb': {
      name: 'Deneb ⚡',
      icon: '⚡',
      color: 'text-cyan-300',
      effect: 'electric-sparkle',
      description: 'Biały supergigant - gwiazda błyskawicznej intuicji'
    }
  };

  // Znaki zodiaku z przypisanymi liczbami szczęśliwymi
  const zodiacSigns = {
    'baran': {
      name: 'Baran ♈',
      luckyNumbers: [1, 9, 14, 27, 33, 41],
      color: 'from-red-500 to-orange-500',
      emoji: '♈'
    },
    'byk': {
      name: 'Byk ♉',
      luckyNumbers: [2, 6, 15, 24, 35, 42],
      color: 'from-green-500 to-emerald-500',
      emoji: '♉'
    },
    'bliznieta': {
      name: 'Bliźnięta ♊',
      luckyNumbers: [3, 12, 18, 25, 36, 43],
      color: 'from-yellow-500 to-amber-500',
      emoji: '♊'
    },
    'rak': {
      name: 'Rak ♋',
      luckyNumbers: [4, 7, 16, 28, 34, 44],
      color: 'from-blue-500 to-cyan-500',
      emoji: '♋'
    },
    'lew': {
      name: 'Lew ♌',
      luckyNumbers: [5, 10, 19, 29, 37, 45],
      color: 'from-orange-500 to-red-500',
      emoji: '♌'
    },
    'panna': {
      name: 'Panna ♍',
      luckyNumbers: [6, 13, 20, 26, 38, 46],
      color: 'from-green-400 to-teal-500',
      emoji: '♍'
    },
    'waga': {
      name: 'Waga ♎',
      luckyNumbers: [7, 11, 17, 30, 39, 47],
      color: 'from-pink-500 to-purple-500',
      emoji: '♎'
    },
    'skorpion': {
      name: 'Skorpion ♏',
      luckyNumbers: [8, 14, 21, 31, 40, 48],
      color: 'from-red-600 to-purple-600',
      emoji: '♏'
    },
    'strzalec': {
      name: 'Strzelec ♐',
      luckyNumbers: [9, 15, 22, 32, 41, 49],
      color: 'from-purple-500 to-indigo-500',
      emoji: '♐'
    },
    'koziorozec': {
      name: 'Koziorożec ♑',
      luckyNumbers: [10, 16, 23, 33, 42, 1],
      color: 'from-gray-600 to-slate-600',
      emoji: '♑'
    },
    'wodnik': {
      name: 'Wodnik ♒',
      luckyNumbers: [11, 17, 24, 34, 43, 2],
      color: 'from-cyan-500 to-blue-500',
      emoji: '♒'
    },
    'ryby': {
      name: 'Ryby ♓',
      luckyNumbers: [12, 18, 25, 35, 44, 3],
      color: 'from-teal-500 to-cyan-500',
      emoji: '♓'
    }
  };

  // Funkcja generowania liczb z preferencją dla znaku zodiaku
  const generateHoroscopeNumbers = (zodiacKey, gameKey) => {
    const zodiac = zodiacSigns[zodiacKey];
    const game = gameConfigs[gameKey];
    if (!zodiac || !game) return { main: [], extra: [] };

    // Generuj liczby główne
    const mainNumbers = new Set();
    const luckyNumbers = zodiac.luckyNumbers.filter(num => num <= game.mainNumbers.max);
    
    // 70% szans na liczby szczęśliwe znaku
    while (mainNumbers.size < game.mainNumbers.count) {
      if (mainNumbers.size < Math.floor(game.mainNumbers.count * 0.7) && Math.random() < 0.7 && luckyNumbers.length > 0) {
        // Preferuj liczby szczęśliwe
        const luckyNum = luckyNumbers[Math.floor(Math.random() * luckyNumbers.length)];
        mainNumbers.add(luckyNum);
      } else {
        // Dodaj losową liczbę
        const randomNum = Math.floor(Math.random() * game.mainNumbers.max) + 1;
        mainNumbers.add(randomNum);
      }
    }
    
    // Generuj liczby dodatkowe (jeśli gra je wymaga)
    const extraNumbers = new Set();
    if (game.extraNumbers) {
      while (extraNumbers.size < game.extraNumbers.count) {
        const randomNum = Math.floor(Math.random() * game.extraNumbers.max) + 1;
        extraNumbers.add(randomNum);
      }
    }
    
    return {
      main: Array.from(mainNumbers).sort((a, b) => a - b),
      extra: Array.from(extraNumbers).sort((a, b) => a - b)
    };
  };

  const handleGenerate = async () => {
    if (!selectedZodiac) return;
    
    setIsGenerating(true);
    setShowResults(false);
    
    // Symulacja ładowania
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const result = generateHoroscopeNumbers(selectedZodiac, selectedGame);
    setGeneratedNumbers(result.main);
    setGeneratedExtraNumbers(result.extra);
    setIsGenerating(false);
    setShowResults(true);
  };

  // Funkcja kopiowania liczb
  const copyNumbers = () => {
    const game = gameConfigs[selectedGame];
    let numbersText = `${game.name}: ${generatedNumbers.join(', ')}`;
    if (generatedExtraNumbers.length > 0) {
      numbersText += ` + ${generatedExtraNumbers.join(', ')}`;
    }
    navigator.clipboard.writeText(numbersText);
    setShowCopySuccess(true);
    setTimeout(() => setShowCopySuccess(false), 2000);
  };

  // Funkcja dodawania do ulubionych (kompatybilna z sekcją w "Moje Konto")
  const addToFavorites = () => {
    const game = gameConfigs[selectedGame];
    const selectedZodiacData = zodiacSigns[selectedZodiac];
    const selectedStarData = starConfigs[selectedStar];
    let numbersText = generatedNumbers.join(', ');
    if (generatedExtraNumbers.length > 0) {
      numbersText += ` + ${generatedExtraNumbers.join(', ')}`;
    }
    
    const favorite = {
      id: Date.now(),
      name: `🔮 ${selectedZodiacData.name} ${selectedStarData?.icon || '⭐'} ${game.name} ${new Date().toLocaleDateString('pl-PL')}`,
      set: numbersText,
      game: selectedGame,
      generatorType: 'horoscope-generator',
      date: new Date().toISOString(),
      zodiac: selectedZodiacData.name,
      star: selectedStarData?.name || '',
      mainNumbers: [...generatedNumbers],
      extraNumbers: [...generatedExtraNumbers]
    };
    
    // Zapisz w localStorage (ten sam klucz co inne ulubione)
    const existingFavorites = JSON.parse(localStorage.getItem('favoriteSets') || '[]');
    const updatedFavorites = [favorite, ...existingFavorites];
    localStorage.setItem('favoriteSets', JSON.stringify(updatedFavorites));
    
    // Pokaż komunikat
    alert(`✅ Zestaw "${favorite.name}" został dodany do ulubionych!`);
  };

  // Funkcja uruchamiania efektu gwiazdy
  const triggerStarEffect = (starKey) => {
    if (!starKey) return;
    
    const star = starConfigs[starKey];
    setActiveStarEffect(star.effect);
    setStarAnimation(true);
    
    // Zatrzymaj efekt po 4 sekundach
    setTimeout(() => {
      setStarAnimation(false);
      setActiveStarEffect('');
    }, 4000);
  };

  const ballVariants = {
    hidden: { 
      y: -100, 
      opacity: 0, 
      scale: 0,
      rotate: 0 
    },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      scale: 1,
      rotate: 360,
      transition: {
        delay: i * 0.2,
        duration: 0.8,
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }),
    bounce: {
      y: [0, -20, 0],
      transition: {
        duration: 0.6,
        repeat: 2,
        ease: "easeInOut"
      }
    }
  };

  const selectedZodiacData = selectedZodiac ? zodiacSigns[selectedZodiac] : null;
  const selectedGameData = gameConfigs[selectedGame];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4 flex flex-col items-center justify-center relative">
      <style>{`
        .bg-gradient-radial {
          background: radial-gradient(circle, var(--tw-gradient-stops));
        }
      `}</style>
      {/* Nagłówek */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        className="text-center mb-8"
      >
        <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent mb-4">
          🔮 Lotto Horoscope
        </h1>
        <p className="text-white/80 text-lg md:text-xl">
          Niech gwiazdy wskażą Ci szczęśliwe liczby!
        </p>
      </motion.div>

      {/* Główna karta */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-xl p-6 md:p-8 w-full max-w-lg border border-white/20"
      >
        {/* Dropdown wyboru gry */}
        <div className="mb-4">
          <label className="block text-white text-sm font-medium mb-3">
            Wybierz grę:
          </label>
          <motion.select
            value={selectedGame}
            onChange={(e) => setSelectedGame(e.target.value)}
            className="w-full bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            whileFocus={{ scale: 1.02 }}
          >
            {Object.entries(gameConfigs).map(([key, game]) => (
              <option key={key} value={key} className="bg-gray-800 text-white">
                {game.name}
              </option>
            ))}
          </motion.select>
        </div>

        {/* Dropdown ze znakami zodiaku */}
        <div className="mb-4">
          <label className="block text-white text-sm font-medium mb-3">
            Wybierz swój znak zodiaku:
          </label>
          <motion.select
            value={selectedZodiac}
            onChange={(e) => setSelectedZodiac(e.target.value)}
            className="w-full bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            whileFocus={{ scale: 1.02 }}
          >
            <option value="" className="bg-gray-800 text-white">
              -- Wybierz znak zodiaku --
            </option>
            {Object.entries(zodiacSigns).map(([key, zodiac]) => (
              <option key={key} value={key} className="bg-gray-800 text-white">
                {zodiac.name}
              </option>
            ))}
          </motion.select>
        </div>

        {/* Dropdown wyboru gwiazdy */}
        <div className="mb-6">
          <label className="block text-white text-sm font-medium mb-3">
            Wybierz gwiazdę przewodnią:
          </label>
          <motion.select
            value={selectedStar}
            onChange={(e) => {
              setSelectedStar(e.target.value);
              triggerStarEffect(e.target.value);
            }}
            className="w-full bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl px-4 py-3 text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
            whileFocus={{ scale: 1.02 }}
          >
            <option value="" className="bg-gray-800 text-white">
              -- Wybierz gwiazdę --
            </option>
            {Object.entries(starConfigs).map(([key, star]) => (
              <option key={key} value={key} className="bg-gray-800 text-white">
                {star.name}
              </option>
            ))}
          </motion.select>
          {selectedStar && (
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-2 text-sm ${starConfigs[selectedStar].color}`}
            >
              {starConfigs[selectedStar].description}
            </motion.p>
          )}
        </div>

        {/* Przycisk generowania */}
        <motion.button
          onClick={handleGenerate}
          disabled={!selectedZodiac || !selectedStar || isGenerating}
          className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
            !selectedZodiac || !selectedStar || isGenerating
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : `bg-gradient-to-r ${selectedGameData?.color || 'from-purple-500 to-pink-500'} text-white hover:shadow-lg hover:scale-105`
          }`}
          whileHover={!selectedZodiac || !selectedStar || isGenerating ? {} : { scale: 1.05 }}
          whileTap={!selectedZodiac || !selectedStar || isGenerating ? {} : { scale: 0.95 }}
        >
          {isGenerating ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Czytam w gwiazdach...</span>
            </div>
          ) : (
            `✨ Wylosuj ${selectedGameData.name} ${selectedZodiacData?.emoji || ''} ${starConfigs[selectedStar]?.icon || ''}`
          )}
        </motion.button>

        {/* Wyniki */}
        <AnimatePresence>
          {showResults && generatedNumbers.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="mt-8"
            >
              <h3 className="text-white text-center text-lg font-semibold mb-4">
                {selectedGameData.name} - {selectedZodiacData?.emoji} {selectedZodiacData?.name}
              </h3>
              
              {/* Liczby główne */}
              <div className="mb-4">
                <p className="text-white/80 text-center text-sm mb-3">
                  {selectedGameData.extraNumbers ? 'Liczby główne:' : 'Twoje szczęśliwe liczby:'}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {generatedNumbers.map((number, index) => (
                    <motion.div
                      key={number}
                      custom={index}
                      variants={ballVariants}
                      initial="hidden"
                      animate={["visible", "bounce"]}
                      className={`w-14 h-14 rounded-full bg-gradient-to-br ${selectedGameData?.color || 'from-purple-500 to-pink-500'} flex items-center justify-center text-white font-bold text-lg shadow-lg border-2 border-white/30`}
                    >
                      {number}
                    </motion.div>
                  ))}
                </div>
              </div>

              {/* Liczby dodatkowe (jeśli istnieją) */}
              {generatedExtraNumbers.length > 0 && (
                <div className="mb-4">
                  <p className="text-white/80 text-center text-sm mb-3">
                    Liczby dodatkowe:
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {generatedExtraNumbers.map((number, index) => (
                      <motion.div
                        key={`extra-${number}`}
                        custom={index + generatedNumbers.length}
                        variants={ballVariants}
                        initial="hidden"
                        animate={["visible", "bounce"]}
                        className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-500 to-amber-500 flex items-center justify-center text-white font-bold text-sm shadow-lg border-2 border-white/30"
                      >
                        {number}
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Przyciski akcji */}
              <div className="flex gap-3 mt-6">
                <motion.button
                  onClick={copyNumbers}
                  className="flex-1 bg-white/20 hover:bg-white/30 text-white py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>📋</span>
                  <span>Kopiuj</span>
                </motion.button>
                
                <motion.button
                  onClick={addToFavorites}
                  className="flex-1 bg-white/20 hover:bg-white/30 text-white py-3 px-4 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>⭐</span>
                  <span>Ulubione</span>
                </motion.button>
              </div>

              {/* Komunikat o skopiowaniu */}
              <AnimatePresence>
                {showCopySuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-3 p-2 bg-green-500/20 border border-green-400/30 rounded-lg text-center"
                  >
                    <p className="text-green-300 text-sm">✅ Skopiowano do schowka!</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Opis znaku */}
              {selectedZodiacData && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.5 }}
                  className="mt-6 p-4 bg-white/10 rounded-xl border border-white/20"
                >
                  <p className="text-white/90 text-sm text-center">
                    <span className="font-semibold">{selectedZodiacData.name}</span>
                    <br />
                    Liczby zostały wybrane na podstawie wpływów planetarnych Twojego znaku zodiaku dla gry {selectedGameData.name}.
                  </p>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Efekty gwiazd */}
      <AnimatePresence>
        {starAnimation && (
          <>
            {/* Aldebaran - czerwony pulsujący glow */}
            {activeStarEffect === 'red-pulse' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: [0, 0.6, 0],
                  scale: [1, 1.2, 1]
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1, repeat: 3 }}
                className="fixed inset-0 bg-red-500/20 pointer-events-none"
              />
            )}

            {/* Betelgeuse - supernowa rozbłysk */}
            {activeStarEffect === 'supernova' && (
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ 
                  opacity: [0, 1, 0],
                  scale: [0, 3, 0]
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 2, repeat: 2 }}
                className="fixed inset-0 bg-gradient-radial from-orange-400/40 via-yellow-300/20 to-transparent pointer-events-none"
              />
            )}

            {/* Rigel - niebieski shimmer */}
            {activeStarEffect === 'blue-shimmer' && (
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: '100%' }}
                exit={{ x: '100%' }}
                transition={{ duration: 2, repeat: 2 }}
                className="fixed inset-0 bg-gradient-to-r from-transparent via-blue-400/30 to-transparent pointer-events-none"
                style={{ transform: 'skewX(-20deg)' }}
              />
            )}

            {/* Vega - spadające gwiazdki */}
            {activeStarEffect === 'star-rain' && (
              <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {[...Array(15)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ 
                      y: window.innerHeight + 50,
                      opacity: [0, 1, 0]
                    }}
                    transition={{
                      duration: 2,
                      delay: i * 0.2,
                      ease: "linear"
                    }}
                    className="absolute text-white text-lg"
                    style={{
                      left: `${Math.random() * 100}%`,
                    }}
                  >
                    ✨
                  </motion.div>
                ))}
              </div>
            )}

            {/* Antares - ognisty spin */}
            {activeStarEffect === 'fire-spin' && (
              <motion.div
                initial={{ opacity: 0, rotate: 0 }}
                animate={{ 
                  opacity: [0, 0.8, 0],
                  rotate: 360,
                  scale: [1, 1.1, 1]
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1, repeat: 4 }}
                className="fixed inset-0 bg-gradient-radial from-red-600/30 via-orange-500/20 to-transparent pointer-events-none"
              />
            )}

            {/* Spica - promień światła */}
            {activeStarEffect === 'light-beam' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ 
                  height: '100%',
                  opacity: [0, 1, 0]
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5, repeat: 2 }}
                className="fixed top-0 left-1/2 w-1 bg-gradient-to-b from-white via-blue-300 to-transparent pointer-events-none transform -translate-x-1/2"
              />
            )}

            {/* Orion - konstelacja */}
            {activeStarEffect === 'constellation' && (
              <div className="fixed inset-0 pointer-events-none">
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ 
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0]
                    }}
                    transition={{
                      duration: 1,
                      delay: i * 0.3,
                      repeat: 2
                    }}
                    className="absolute text-purple-300 text-2xl"
                    style={{
                      left: `${20 + (i * 10)}%`,
                      top: `${20 + (i % 3) * 20}%`,
                    }}
                  >
                    ⭐
                  </motion.div>
                ))}
              </div>
            )}

            {/* Deneb - elektryczne iskry */}
            {activeStarEffect === 'electric-sparkle' && (
              <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ 
                      opacity: [0, 1, 0],
                      scale: [0, 1.5, 0],
                      rotate: [0, 180, 360]
                    }}
                    transition={{
                      duration: 0.5,
                      delay: i * 0.1,
                      repeat: 8
                    }}
                    className="absolute text-cyan-300 text-sm"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                    }}
                  >
                    ⚡
                  </motion.div>
                ))}
              </div>
            )}
          </>
        )}
      </AnimatePresence>

      {/* Dodatkowe elementy dekoracyjne - podstawowe migotanie */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-white/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [0, 1, 0],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default LottoHoroscope;
